/**
 * @file
 * Partner modal behavior.
 *
 * Handles modal interactions for partner detail pages loaded via HTMX,
 * including focus management and gallery initialization.
 *
 * @param {object} Drupal - The Drupal object.
 * @param {Function} once - The once function.
 */
(function (Drupal, once) {
  'use strict';

  const CLOSE_FALLBACK_DELAY = 500;
  const MODAL_OPEN_CLASS = 'partner-modal-is-open';
  const FOCUSABLE_SELECTORS = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(', ');

  let activeTrigger = null;

  Drupal.behaviors.partnerModalBehavior = {

    /**
     * Attaches Partner Modal behavior once per modal instance.
     *
     * @param {Document|HTMLElement} context - The Drupal behavior context.
     * @param {object} settings - The Drupal settings object.
     */
    attach: function (context, settings) {
      const MODAL_ID = 'partner-detail-modal';

      // Initialize modal close handlers
      once('partner-modal-init', `#${MODAL_ID}`, context).forEach(modal => {

        // Close button handler
        const closeBtn = modal.querySelector('.modal-close');
        if (closeBtn) {
          closeBtn.addEventListener('click', () => {
            this.closeModal(modal);
          });
        }

        // Click outside to close
        modal.addEventListener('click', (event) => {
          if (event.target === modal) {
            this.closeModal(modal);
          }
        });

        // ESC key to close
        modal.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            this.closeModal(modal);
            return;
          }

          if (event.key === 'Tab') {
            this.trapModalFocus(modal, event);
          }
        });

        modal.addEventListener('cancel', (event) => {
          event.preventDefault();
          this.closeModal(modal);
        });

        // Clean up content when modal closes
        modal.addEventListener('close', () => {
          const content = modal.querySelector('.partner-modal__content');
          if (content) {
            content.innerHTML = '';
          }
          // Remove loading class if present
          modal.classList.remove('loading');
          document.body.classList.remove(MODAL_OPEN_CLASS);
          document.documentElement.classList.remove('modal-closing');
        });

        // Handle htmx events on the modal content div
        const modalContent = modal.querySelector('.partner-modal__content');
        if (modalContent) {
          // Listen for htmx events on the content div (the target)
          modalContent.addEventListener('htmx:beforeSwap', (event) => {
            activeTrigger = this.getModalTrigger(event);
            modal.classList.add('loading');
          });

          modalContent.addEventListener('htmx:afterSwap', () => {
            this.openModal(modal, modalContent);
          });

          modalContent.addEventListener('htmx:responseError', () => {
            this.handleModalError(modal, modalContent);
          });
        }
      });
    },

    /**
     * Closes the modal after its transition and restores the trigger focus.
     *
     * @param {HTMLDialogElement} modal - The partner modal dialog.
     */
    closeModal: function (modal) {
      if (!modal || !modal.open || document.documentElement.classList.contains('modal-closing')) {
        return;
      }
      let modalClosed = false;
      let fallbackTimer = null;

      const finishClose = () => {
        if (modalClosed) {
          return;
        }

        modalClosed = true;
        window.clearTimeout(fallbackTimer);
        modal.close();
        document.body.classList.remove(MODAL_OPEN_CLASS);
        document.documentElement.classList.remove('modal-closing');
        modal.removeEventListener('transitionend', finishClose);
        this.restoreTriggerFocus();
      };

      document.documentElement.classList.add('modal-closing');
      modal.addEventListener('transitionend', finishClose);
      // Fallback in case transitionend is skipped or interrupted.
      fallbackTimer = window.setTimeout(finishClose, CLOSE_FALLBACK_DELAY);

    },

    /**
     * Gets the element that triggered the HTMX modal request.
     *
     * @param {CustomEvent} event - The HTMX lifecycle event.
    *
    * @return {HTMLElement|null} The modal trigger element, when available.
     */
    getModalTrigger: function (event) {
      if (event.detail && event.detail.requestConfig && event.detail.requestConfig.elt instanceof HTMLElement) {
        return event.detail.requestConfig.elt;
      }

      if (event.detail && event.detail.elt instanceof HTMLElement) {
        return event.detail.elt;
      }

      if (document.activeElement instanceof HTMLElement) {
        return document.activeElement;
      }

      return null;
    },

    /**
     * Opens the modal and initializes dynamic content behaviors.
     *
     * @param {HTMLDialogElement} modal - The partner modal dialog.
     * @param {HTMLElement} modalContent - The modal content container.
     */
    openModal: function (modal, modalContent) {
      modal.classList.remove('loading');
      this.setModalDescription(modal);
      document.body.classList.add(MODAL_OPEN_CLASS);

      if (!modal.open) {
        modal.showModal();
      }

      window.requestAnimationFrame(() => {
        if (Drupal.attachBehaviors) {
          Drupal.attachBehaviors(modalContent, window.drupalSettings);
        }

        this.focusModalContent(modal);
      });
    },

    /**
     * Restores focus to the element that opened the modal.
     */
    restoreTriggerFocus: function () {
      if (!activeTrigger || !document.contains(activeTrigger)) {
        activeTrigger = null;
        return;
      }

      activeTrigger.focus({preventScroll: true});
      activeTrigger = null;
    },

    /**
     * Updates aria-describedby when the loaded modal content has a description.
     *
     * @param {HTMLDialogElement} modal - The partner modal dialog.
     */
    setModalDescription: function (modal) {
      const description = modal.querySelector('#partner-detail-modal-description');

      if (!description) {
        modal.removeAttribute('aria-describedby');
        return;
      }

      modal.setAttribute('aria-describedby', description.id);
    },

    /**
     * Gets visible, keyboard-focusable elements inside the modal.
     *
     * @param {HTMLDialogElement} modal - The partner modal dialog.
    *
    * @return {HTMLElement[]} The visible focusable elements.
     */
    getFocusableElements: function (modal) {
      return Array.from(modal.querySelectorAll(FOCUSABLE_SELECTORS)).filter((element) => {
        return element.getClientRects().length > 0 && element.getAttribute('aria-hidden') !== 'true';
      });
    },

    /**
     * Keeps Tab and Shift+Tab focus navigation inside the open modal.
     *
     * @param {HTMLDialogElement} modal - The partner modal dialog.
     * @param {KeyboardEvent} event - The keyboard event to handle.
     */
    trapModalFocus: function (modal, event) {
      const focusableElements = this.getFocusableElements(modal);

      if (!focusableElements.length) {
        event.preventDefault();
        modal.focus({preventScroll: true});
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const isFocusOutsideModal = !modal.contains(document.activeElement);
      const shouldFocusLast = event.shiftKey && document.activeElement === firstElement;
      const shouldFocusFirst = !event.shiftKey && document.activeElement === lastElement;

      if (isFocusOutsideModal) {
        event.preventDefault();
        const nextElement = event.shiftKey ? lastElement : firstElement;
        nextElement.focus({preventScroll: true});
        return;
      }

      if (shouldFocusLast) {
        event.preventDefault();
        lastElement.focus({preventScroll: true});
        return;
      }

      if (shouldFocusFirst) {
        event.preventDefault();
        firstElement.focus({preventScroll: true});
      }
    },

    /**
     * Moves focus to the first useful element after the modal opens.
     *
     * @param {HTMLDialogElement} modal - The partner modal dialog.
     */
    focusModalContent: function (modal) {
      requestAnimationFrame(() => {
        // Priority 1: Close button for easy dismissal
        const closeBtn = modal.querySelector('.modal-close');
        // Priority 2: First heading for context
        const heading = modal.querySelector('h1, h2, h3, h4, h5, h6');
        // Priority 3: Any focusable element
        const focusable = modal.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');

        const elementToFocus = closeBtn || heading || focusable;
        if (!elementToFocus) {
          return;
        }

        if (elementToFocus.matches(FOCUSABLE_SELECTORS)) {
          elementToFocus.focus({preventScroll: true});
          return;
        }

        if (elementToFocus) {
          elementToFocus.setAttribute('tabindex', '-1');
          elementToFocus.focus({preventScroll: true});
          // Remove tabindex after focus to maintain natural tab order
          const handleBlur = () => {
            elementToFocus.removeAttribute('tabindex');
          };
          elementToFocus.addEventListener('blur', handleBlur, {once: true});
        }
      });
    },

    /**
     * Renders and announces a modal loading error.
     *
     * @param {HTMLDialogElement} modal - The partner modal dialog.
     * @param {HTMLElement} modalContent - The modal content container.
     */
    handleModalError: function (modal, modalContent) {
      modalContent.innerHTML = '<div id="partner-detail-modal-description" class="error-message" role="alert" tabindex="-1"><h2 id="partner-detail-modal-title">Unable to load partner details</h2><p>Please try again.</p></div>';
      this.openModal(modal, modalContent);

      // Focus error message for screen reader announcement
      requestAnimationFrame(() => {
        const errorMsg = modalContent.querySelector('.error-message');
        if (errorMsg) {
          errorMsg.focus();
        }
      });
    }
  };

  /**
   * Adjust gallery spacing within partner modal.
   *
   * @type {object}
   */
  Drupal.behaviors.partnerModalGalleryBehavior = {
    attach: function (context) {
      once('partner-modal-gallery', '.partner-detail__media .gallery > .swiper', context).forEach((gallery) => {
        if (gallery.swiper) {
          gallery.swiper.params.spaceBetween = 16;
          gallery.swiper.update();
        }
      });
    }
  };
})(Drupal, once);
